// src/main.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { initApiClient } from './api/client';
import { setAuthState, getRawAuthState } from './api/utils/tokenRefreshContext';
import { loadRuntimeConfig } from './config/runtime-config';
import App from './App';
import './styles/global.css';
import './styles/antd-theme.less';

/**
 * Initialize auth state from localStorage on app startup.
 * This is critical because the tokenRefreshContext needs to have the tokens
 * in memory for the Axios interceptor to attach them to requests.
 */
function initializeAuthFromStorage(): void {
  if (typeof window === 'undefined') return;
  
  const raw = localStorage.getItem('auth');
  if (!raw) {
    console.log('[Auth] No auth data found in localStorage');
    return;
  }
  
  try {
    const parsed = JSON.parse(raw) as {
      accessToken: string;
      refreshToken: string;
      expiresAt: string; // ISO string timestamp
      user: any;
    };
    
    // Parse the absolute expiresAt timestamp
    const expiresAt = new Date(parsed.expiresAt);
    const now = Date.now();
    const isTokenValid = expiresAt.getTime() > now;
    const timeUntilExpiry = ((expiresAt.getTime() - now) / 1000).toFixed(0);
    
    console.log('[Auth] Found auth data:', {
      hasAccessToken: !!parsed.accessToken,
      hasRefreshToken: !!parsed.refreshToken,
      expiresAt: expiresAt.toISOString(),
      timeUntilExpiry: `${timeUntilExpiry}s`,
      isValid: isTokenValid,
    });
    
    if (isTokenValid) {
      // Initialize tokenRefreshContext with the parsed data
      setAuthState({
        accessToken: parsed.accessToken,
        refreshToken: parsed.refreshToken,
        expiresAt,
        user: parsed.user,
        isAuthenticated: true,
      });
      console.log('[Auth] Auth state initialized successfully');
    } else {
      // Token expired, clear localStorage
      console.log('[Auth] Token expired, clearing localStorage');
      localStorage.removeItem('auth');
    }
  } catch (e) {
    console.error('[Auth] Failed to parse auth data from localStorage', e);
    // ignore malformed data
    localStorage.removeItem('auth');
  }
}

/**
 * Main application entry point.
 * Loads runtime configuration before initializing the app.
 */
async function bootstrap(): Promise<void> {
  // Load runtime configuration from config.json
  const config = await loadRuntimeConfig();
  
  // Initialize the API client with the correct base URL from runtime config
  console.log('[API] Initializing client with base URL:', config.apiUrl);
  initApiClient({
    baseUrl: config.apiUrl
  });

  // Initialize auth state from localStorage BEFORE rendering the app
  initializeAuthFromStorage();

  // Verify auth state after initialization
  const authState = getRawAuthState();
  console.log('[App] Final auth state after initialization:', {
    isAuthenticated: !!authState?.accessToken,
    hasAccessToken: !!authState?.accessToken,
    hasRefreshToken: !!authState?.refreshToken,
  });

  // Render the app
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// Start the application
bootstrap().catch((error) => {
  console.error('[Bootstrap] Failed to start application:', error);
  // Display error to user
  const rootElement = document.getElementById('root');
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="padding: 20px; font-family: sans-serif;">
        <h1>Application Error</h1>
        <p>Failed to load application configuration. Please check your connection and try again.</p>
        <button onclick="location.reload()">Retry</button>
      </div>
    `;
  }
});